import React, { FC, memo, useContext, useMemo } from 'react'
import { ScreenContext } from 'react-components'

import {
    Container,
    ImageContainer,
    ImageTopLeftContainer,
    ImageTopRightContainer,
    InformationContainer,
    BottomLeft,
    TopLeft,
    BottomRight,
    TopRight,
} from './horizontal-card-styles'
import ImageCarousel from '../common/image-carousel'
import ImageTransport from '../../transports/common/image-transport'
import { getContent } from '../utils'
import HorizontalCardImageLeftTop from './horizontal-card-components/horizontal-card-image-left-top'
import HorizontalCardLeftTop from './horizontal-card-components/horizontal-card-left-top'
import HorizontalCardRightTop from './horizontal-card-components/horizontal-card-right-top'
import HorizontalCardRightBottom from './horizontal-card-components/horizontal-card-right-bottom'
import HorizontalCardLeftBottom from './horizontal-card-components/horizontal-card-left-bottom'
import HorizontalCardImageContainer from './horizontal-card-components/horizontal-card-image-container'
import HorizontalCardImageRightTop from './horizontal-card-components/horizontal-card-image-right-top'

type props = {
    children: React.JSX.Element | React.ReactNode
    images?: ImageTransport[]
    mainRowConfiguration?: string
    mainColumnConfiguration?: string
    imageCarouselBorderRadius?: string
    imageCarouselMargin?: string
    rowConfiguration?: string
    columnConfiguration?: string
    height?: string
    topLeftGridRow?: string
    topLeftGridColumn?: string
    topRightGridRow?: string
    topRightGridColumn?: string
    bottomLeftGridRow?: string
    bottomLeftGridColumn?: string
    bottomRightGridRow?: string
    bottomRightGridColumn?: string
    aspectRatio?: string
}

const HorizontalCard: FC<props> = ({
    children,
    imageCarouselBorderRadius,
    imageCarouselMargin,
    images,
    mainRowConfiguration,
    mainColumnConfiguration,
    rowConfiguration,
    columnConfiguration,
    height,
    topLeftGridRow,
    topLeftGridColumn,
    topRightGridRow,
    topRightGridColumn,
    bottomLeftGridRow,
    bottomLeftGridColumn,
    bottomRightGridRow,
    bottomRightGridColumn,
    aspectRatio,
}) => {
    const { isTablet } = useContext(ScreenContext)
    const childrenContent = children
    const imageTopLeftContent = useMemo(
        () => getContent(childrenContent, HorizontalCardImageLeftTop),
        [childrenContent],
    )

    const imageTopRightContent = useMemo(
        () => getContent(childrenContent, HorizontalCardImageRightTop),
        [childrenContent],
    )

    const imageContent = useMemo(() => getContent(childrenContent, HorizontalCardImageContainer), [childrenContent])
    const topLeftContent = useMemo(() => getContent(childrenContent, HorizontalCardLeftTop), [childrenContent])
    const topRightContent = useMemo(() => getContent(childrenContent, HorizontalCardRightTop), [childrenContent])
    const bottomLeftContent = useMemo(() => getContent(childrenContent, HorizontalCardLeftBottom), [childrenContent])
    const bottomRightContent = useMemo(() => getContent(childrenContent, HorizontalCardRightBottom), [childrenContent])

    return (
        <Container rowConfiguration={mainRowConfiguration} columnConfiguration={mainColumnConfiguration}>
            <ImageContainer margin={isTablet ? '' : imageCarouselMargin}>
                {!!imageTopLeftContent && <ImageTopLeftContainer>{imageTopLeftContent}</ImageTopLeftContainer>}
                {!!imageTopRightContent && <ImageTopRightContainer>{imageTopRightContent}</ImageTopRightContainer>}
                {images ? (
                    <ImageCarousel
                        imageCarouselMargin={imageCarouselMargin}
                        images={images}
                        horizontalCardHeight={height}
                        aspectRatio={aspectRatio}
                        imageCarouselBorderRadius={imageCarouselBorderRadius}
                    />
                ) : (
                    imageContent
                )}
            </ImageContainer>
            <InformationContainer
                $height={height}
                rowConfiguration={rowConfiguration}
                columnConfiguration={columnConfiguration}
            >
                {!!topLeftContent && (
                    <TopLeft gridRow={topLeftGridRow} gridColumn={topLeftGridColumn}>
                        {topLeftContent}
                    </TopLeft>
                )}
                {!!topRightContent && (
                    <TopRight gridRow={topRightGridRow} gridColumn={topRightGridColumn}>
                        {topRightContent}
                    </TopRight>
                )}
                {bottomLeftContent && (
                    <BottomLeft gridRow={bottomLeftGridRow} gridColumn={bottomLeftGridColumn}>
                        {bottomLeftContent}
                    </BottomLeft>
                )}
                {bottomRightContent && (
                    <BottomRight gridRow={bottomRightGridRow} gridColumn={bottomRightGridColumn}>
                        {bottomRightContent}
                    </BottomRight>
                )}
            </InformationContainer>
        </Container>
    )
}

export default memo(HorizontalCard)
